const TR_MONTHS = [
  "Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran",
  "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık",
];

const atNoon = (date) => {
  const d = new Date(date);
  d.setHours(12, 0, 0, 0);
  return d;
};

const toIsoDateString = (date) => {
  const d = atNoon(date);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

/** ISO hafta yılı ve hafta numarası (Pazartesi başlangıç) */
export const getIsoWeekAndYear = (dateInput = new Date()) => {
  const date = atNoon(dateInput);
  const day = date.getDay() || 7;
  const monday = new Date(date);
  monday.setDate(date.getDate() - (day - 1));

  const thursday = new Date(monday);
  thursday.setDate(monday.getDate() + 3);
  const isoYear = thursday.getFullYear();

  const jan4 = atNoon(new Date(isoYear, 0, 4));
  const jan4Day = jan4.getDay() || 7;
  const week1Monday = new Date(jan4);
  week1Monday.setDate(jan4.getDate() - (jan4Day - 1));

  const week = Math.round((monday - week1Monday) / (7 * 86400000)) + 1;
  return { isoYear, week, monday };
};

export const getMaxIsoWeekForYear = (isoYear) => getIsoWeekAndYear(new Date(isoYear, 11, 28)).week;

export const getIsoWeekRange = (isoYear, week) => {
  const jan4 = atNoon(new Date(isoYear, 0, 4));
  const jan4Day = jan4.getDay() || 7;
  const week1Monday = new Date(jan4);
  week1Monday.setDate(jan4.getDate() - (jan4Day - 1));
  const monday = new Date(week1Monday);
  monday.setDate(week1Monday.getDate() + (week - 1) * 7);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  return {
    start: toIsoDateString(monday),
    end: toIsoDateString(sunday),
    monday,
    sunday,
  };
};

export const getCurrentIsoWeekContext = (dateInput = new Date()) => {
  const { isoYear, week } = getIsoWeekAndYear(dateInput);
  const range = getIsoWeekRange(isoYear, week);
  return { isoYear, week, ...range };
};

export const isFutureIsoWeek = (isoYear, week, now = new Date()) => {
  const cur = getIsoWeekAndYear(now);
  if (isoYear > cur.isoYear) return true;
  if (isoYear === cur.isoYear && week > cur.week) return true;
  return false;
};

export const resolveIsoYearForRow = (row, fallbackIsoYear) => {
  if (row?.report_date) {
    const parsed = String(row.report_date).slice(0, 10);
    if (/^\d{4}-\d{2}-\d{2}$/.test(parsed)) {
      return getIsoWeekAndYear(new Date(parsed.replace(/-/g, "/"))).isoYear;
    }
  }
  return fallbackIsoYear;
};

export const normalizeIsoWeekPair = (isoYear, week, fallback) => {
  const y = Number(isoYear);
  const w = Number(week);
  const fb = fallback ?? getCurrentIsoWeekContext();
  return {
    isoYear: Number.isFinite(y) && y >= 2000 && y <= 2100 ? y : fb.isoYear,
    week: Number.isFinite(w) && w >= 1 && w <= 53 ? w : fb.week,
  };
};

export const formatWeekRangeLabel = (isoYear, week) => {
  const { isoYear: y, week: w } = normalizeIsoWeekPair(isoYear, week);
  if (!y || !w) return "—";
  const range = getIsoWeekRange(y, w);
  return formatDateRangeTurkish(range.monday, range.sunday, y);
};

export const formatDateRangeTurkish = (startDate, endDate, yearHint = null) => {
  const start = atNoon(startDate);
  const end = atNoon(endDate);
  const y = yearHint ?? end.getFullYear();
  const sameMonth = start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear();
  if (start.getTime() === end.getTime()) {
    return `${start.getDate()} ${TR_MONTHS[start.getMonth()]} ${y}`;
  }
  if (sameMonth) {
    return `${start.getDate()}-${end.getDate()} ${TR_MONTHS[start.getMonth()]} ${y}`;
  }
  return `${start.getDate()} ${TR_MONTHS[start.getMonth()]} - ${end.getDate()} ${TR_MONTHS[end.getMonth()]} ${y}`;
};

export const weekOptionKey = (isoYear, week) => `${isoYear}-w-${week}`;

export const parseWeekOptionKey = (key) => {
  const m = String(key || "").match(/^(\d{4})-w-(\d{1,2})$/);
  if (!m) return { isoYear: NaN, week: NaN };
  return { isoYear: Number(m[1]), week: Number(m[2]) };
};

/** Filtre için seçilebilir yıllar (gelecek yıl yok) */
export const listSelectableIsoYears = (now = new Date(), pastYears = 3) => {
  const current = getIsoWeekAndYear(now).isoYear;
  const years = [];
  for (let y = current - pastYears; y <= current; y += 1) {
    years.push(y);
  }
  return years;
};

export const listSelectableWeeks = (isoYear, now = new Date()) => {
  const max = getMaxIsoWeekForYear(isoYear);
  const cur = getIsoWeekAndYear(now);
  const cap = isoYear === cur.isoYear ? cur.week : max;
  const weeks = [];
  for (let w = 1; w <= cap; w += 1) {
    weeks.push(w);
  }
  return weeks;
};

/** Tüm seçilebilir haftalar (yeni → eski), etiket: 11-17 Mayıs 2026 */
export const listSelectableWeekOptions = (now = new Date(), pastYears = 3) => {
  const years = listSelectableIsoYears(now, pastYears);
  const options = [];
  years.forEach((isoYear) => {
    listSelectableWeeks(isoYear, now).forEach((week) => {
      options.push({
        isoYear,
        week,
        key: weekOptionKey(isoYear, week),
        label: formatWeekRangeLabel(isoYear, week),
      });
    });
  });
  options.sort((a, b) => {
    const ra = getIsoWeekRange(a.isoYear, a.week);
    const rb = getIsoWeekRange(b.isoYear, b.week);
    return rb.start.localeCompare(ra.start);
  });
  return options;
};

/** Seçili hafta listede yoksa (eski kayıt) seçeneklere ekler — Radix Select hatasını önler */
export const listWeekOptionsIncludingSelection = (isoYear, week, now = new Date(), pastYears = 3) => {
  const base = listSelectableWeekOptions(now, pastYears);
  const { isoYear: y, week: w } = normalizeIsoWeekPair(isoYear, week, getCurrentIsoWeekContext(now));
  const key = weekOptionKey(y, w);
  if (base.some((o) => o.key === key)) return base;
  if (isFutureIsoWeek(y, w, now)) return base;
  return [
    {
      isoYear: y,
      week: w,
      key,
      label: formatWeekRangeLabel(y, w),
    },
    ...base,
  ];
};
